import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API });

// Open an uploaded file (e.g. a product spec PDF) that is served behind auth.
// `path` is the stored spec_pdf_url, e.g. "/api/uploads/<name>". We fetch it
// with the Bearer token, then open the resulting blob in a new tab.
export async function openProtectedFile(path) {
  // Stored paths may be "/api/uploads/<name>" (current) or "/uploads/<name>"
  // (legacy). Both are served by the authenticated GET /api/uploads/<name>.
  const name = String(path).split("/").pop();
  const res = await api.get(`/uploads/${name}`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  window.open(url, "_blank", "noopener");
  // Revoke after a delay so the new tab has time to load it.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// Fetch a protected endpoint that returns a file and save it to disk via the
// browser's download UI (as opposed to openProtectedFile, which opens it in
// a tab). Used e.g. to grab the quotation PDF before attaching it manually
// to a Gmail draft — Gmail's compose URL has no attachment parameter.
export async function downloadProtectedFile(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const TOKEN_KEY = "papertrade_token";
const USER_KEY = "papertrade_user";

let _onAuthError = null;
export const setAuthErrorHandler = (fn) => { _onAuthError = fn; };

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      if (_onAuthError) _onAuthError();
    }
    return Promise.reject(err);
  }
);

const AuthCtx = createContext({ user: null, login: () => {}, logout: () => {} });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); } catch { return null; }
  });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setAuthErrorHandler(() => setUser(null));
    // Verify session on mount
    if (localStorage.getItem(TOKEN_KEY)) {
      api.get("/auth/me").then((r) => {
        setUser(r.data);
        localStorage.setItem(USER_KEY, JSON.stringify(r.data));
      }).catch(() => {
        setUser(null);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }).finally(() => setReady(true));
    } else {
      setReady(true);
    }
  }, []);

  const loginWithGoogle = async (credential) => {
    const { data } = await api.post("/auth/google", { credential });
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    // Best-effort: record the logout event before clearing the token.
    api.post("/auth/logout").catch(() => {}).finally(() => {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setUser(null);
    });
  };

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    localStorage.setItem(USER_KEY, JSON.stringify(data));
    return data;
  };

  const isAdmin = user?.role === "admin";

  return <AuthCtx.Provider value={{ user, isAdmin, loginWithGoogle, logout, ready, refreshUser }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
